import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetRFACustomerProductForTenantQuery } from "../../slices/ExploreCustomers.slice";
import RFACustomerProductCharts from "./RFACustomerProductCharts";
import RFACustomerProductTable from "./RFACustomerProductTable";

const RFACustomerProductContainer = (props: any) => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const { data, error, isError, isFetching, isSuccess } = useGetRFACustomerProductForTenantQuery(tenantId);
  const [filteredTableItems, setFilteredTableItems] = useState([]);
  // Change: chartFilters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const displayAPIException = (isError && <DisplayAPIsException error={error} height={250} />) || "";

  // Evetns
  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`RFA product details are not available for ${props.tenantName}.`}</div>;
  };

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  // Hooks
  useEffect(() => {
    logEvent("RFA Tab - Customer Engagements", "Customer Page", "RFACustomerProductContainer", customProps);
  }, []);

  useEffect(() => {
    setFilteredTableItems(data?.table?.rfaCustomerProductList ?? []);
    if (isError) {
      logException("Customer Page - Customer Engagements Tab - RFA Sub Tab", "RFACustomerProductContainer", "useGetRFACustomerProductForTenantQuery", error, customProps);
    }
  }, [data, isError]);

  useEffect(() => {
    const filteredData = (data?.table?.rfaCustomerProductList || []).filter(
      ({ rfaStatus, requestType, productName }: any) => {
        // If no filters, show all data
        if (chartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return chartFilters.every((filter) => {
          if (filter.filterKey === "rfaStatus") {
            return rfaStatus === filter.value;
          } else if (filter.filterKey === "productName") {
            return productName === filter.value;
          } else if (filter.filterKey === "requestType") {
            return requestType === filter.value;
          }
          return true;
        });
      }
    );

    setFilteredTableItems(filteredData);
  }, [chartFilters, data]);

  return (
    <>
      <RFACustomerProductCharts
        loading={isFetching}
        onSelectedChartFilter={handleChartFilter}
        options={{
          filteredData: filteredTableItems,
          originalData: data?.table?.rfaCustomerProductList || [],
          chartOptions: [
            {
              id: 1,
              field: "productName",
              key: "customer-view-customer-engagements-rfa-product-chart-by-productName",
              title: "Product Name",
              type: "donut",
              kustoLink: data?.table?.productKustoLink
            },
            {
              id: 2,
              field: "rfaStatus",
              key: "customer-view-customer-engagements-rfa-product-chart-by-rfaStatus",
              title: "RFA Status",
              type: "donut",
              kustoLink: data?.table?.rfaStatusKustoLink
            },
            {
              id: 3,
              field: "requestType",
              key: "customer-view-customer-engagements-rfa-product-chart-by-requestType",
              title: "Request Type",
              type: "donut",
              kustoLink: data?.table?.requestTypeKustoLink
            }
          ]
        }}
      />
      {isFetching && !filteredTableItems?.length ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (isSuccess && Array.isArray(filteredTableItems) && filteredTableItems.length && (
          <RFACustomerProductTable
            tableData={filteredTableItems}
            tenantName={props.tenantName}
            rfaCustomerProductKustoLink={data?.table?.rfaCustomerProductListKustoLink}
          />
        )) ||
        (isSuccess && Array.isArray(filteredTableItems) && filteredTableItems.length === 0 && showNoData())
      )}
      {displayAPIException}
    </>
  );
};

export default RFACustomerProductContainer;
